import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, ReceiptText, Wallet, ClipboardCheck, RefreshCcw, BadgeCheck, Trash2 } from "lucide-react";
import api, { formatErr, inr, fmtDay } from "../../lib/api";
import { Loading, PageHeader, StatusPill, EmptyState, ConfirmDelete } from "../../components/Shared";
import { useAuth } from "../../context/AuthContext";

const METHODS = [
  { v: "bank_transfer", label: "Bank transfer (NEFT/IMPS/RTGS)" },
  { v: "upi", label: "UPI" },
  { v: "cash", label: "Cash" },
  { v: "other", label: "Other approved manual method" },
];

function downloadReceipt(inv) {
  const text = `PARENTONE CARE PRIVATE LIMITED\nReceipt ${inv.number}\n${inv.description}\nAmount: ${inr(inv.amount)} + GST ${inr(inv.gst)}\nTotal: ${inr(inv.total)}\nStatus: ${inv.status}\nIssued: ${fmtDay(inv.issued_at)}${inv.paid_at ? `\nPaid: ${fmtDay(inv.paid_at)}` : ""}`;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
  a.download = `${inv.number}.txt`; a.click();
}

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <span className="text-stone-500">{label}</span>
      <span className="text-right font-semibold text-stone-800">{children ?? "—"}</span>
    </div>
  );
}

function BillingPanel({ fid, onClose, canManage, onChanged }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [b, setB] = useState(null);
  const [mode, setMode] = useState(null); // assign | payment | change
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ plan_id: "", amount: "", method: "bank_transfer", reference: "", note: "" });
  const [confirmDel, setConfirmDel] = useState(null); // "family" or a parent object
  const load = () => api.get(`/admin/customers/${fid}/billing`).then((r) => setB(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, [fid]);
  if (!b) return <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50"><Loading /></div>;

  const { family, subscription: sub, plan, plans = [], invoices = [], payments = [], care_manager, activity = [], latest_invoice } = b;
  const canAct = canManage && b.can_manage_billing;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); setMode(null); setQuote(null); await load(); onChanged?.(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const markAssessment = () => run(() => api.post(`/admin/customers/${fid}/assessment-complete`, {}), "Assessment marked completed");
  const assignPlan = () => run(() => api.post(`/admin/customers/${fid}/assign-plan`, { plan_id: form.plan_id }), "Plan assigned");
  const recordPayment = () => run(() => api.post(`/admin/customers/${fid}/record-payment`, {
    plan_id: form.plan_id || sub?.plan_id, amount: form.amount ? Number(form.amount) : undefined,
    method: form.method, reference: form.reference, note: form.note,
  }), "Offline payment recorded");
  const activate = () => run(() => api.post(`/admin/customers/${fid}/activate-subscription`, {}), "Subscription activated");
  const requestQuote = (plan_id) => run(async () => {
    const r = await api.post(`/admin/customers/${fid}/change-plan`, { plan_id });
    setQuote({ ...r.data.quote, plan_id });
  }, "Quote ready");
  const confirmChange = () => run(() => api.post(`/admin/customers/${fid}/change-plan`, {
    plan_id: quote.plan_id, confirm: true,
    method: quote.type === "upgrade" && form.method ? form.method : undefined,
    reference: form.reference || undefined,
  }), quote.type === "upgrade" ? "Plan upgraded" : "Downgrade scheduled");

  const deleteAccount = async () => {
    setBusy(true);
    try {
      if (confirmDel === "family") {
        await api.delete(`/admin/customers/${fid}`);
        toast.success("Customer account permanently deleted");
        setConfirmDel(null); onClose(); onChanged?.();
      } else {
        await api.delete(`/admin/customers/${fid}/parents/${confirmDel.id}`);
        toast.success("Parent profile permanently deleted");
        setConfirmDel(null); await load();
      }
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const changeStatus = (status) => run(() => api.patch(`/admin/customers/${fid}`, { status }), "Customer status updated");

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-stone-900/50 p-4" data-testid="billing-panel">
      <div className="my-8 w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold text-stone-900">Subscription & Billing — {family.name}</h2>
            <p className="text-sm text-stone-500">{family.city} · Family ID {family.id}</p>
          </div>
          <button onClick={onClose} className="rounded-full border border-stone-200 px-4 py-1.5 text-xs font-semibold text-stone-500" data-testid="billing-panel-close">Close</button>
        </div>

        <div className="mt-5 grid gap-6 sm:grid-cols-2">
          <div className="rounded-2xl border border-stone-200 p-4">
            <Row label="Assessment status"><StatusPill status={b.assessment_status} /></Row>
            <Row label="Selected / current plan">{plan ? `${plan.name} · ${inr(plan.price)}/mo` : "None"}</Row>
            <Row label="Subscription status">{sub ? <StatusPill status={sub.status} /> : "No subscription"}</Row>
            <Row label="Payment status">{latest_invoice ? <StatusPill status={latest_invoice.status} /> : "—"}</Row>
            <Row label="Payment method">{payments[0] ? payments[0].method.replace(/_/g, " ") : "—"}</Row>
            <Row label="Transaction reference">{payments[0]?.reference || "—"}</Row>
            <Row label="Start date">{sub?.started_at ? fmtDay(sub.started_at) : "—"}</Row>
            <Row label="Renewal date">{sub?.renews_at ? fmtDay(sub.renews_at) : "—"}</Row>
            {sub?.pending_plan_id && <Row label="Scheduled change">{b.pending_plan?.name || sub.pending_plan_id} on {fmtDay(sub.pending_change_effective_at)}</Row>}
            <Row label="Care Manager">{care_manager?.name || "Unassigned"}</Row>
          </div>

          <div>
            {canAct ? (
              <div className="space-y-2" data-testid="billing-actions">
                {b.assessment_status === "assessment_pending" && (
                  <button onClick={markAssessment} disabled={busy} data-testid="action-assessment-complete"
                    className="flex w-full items-center gap-2 rounded-xl bg-emerald-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-50">
                    <ClipboardCheck className="h-4 w-4" /> Mark Assessment Completed
                  </button>
                )}
                {(!sub || sub.status !== "active") && (
                  <button onClick={() => setMode(mode === "assign" ? null : "assign")} data-testid="action-assign-plan"
                    className="flex w-full items-center gap-2 rounded-xl border border-emerald-800 px-4 py-2.5 text-sm font-bold text-emerald-900 hover:bg-emerald-50">
                    <BadgeCheck className="h-4 w-4" /> Assign Plan
                  </button>
                )}
                {(!sub || sub.status !== "active") && (
                  <button onClick={() => setMode(mode === "payment" ? null : "payment")} data-testid="action-record-payment"
                    className="flex w-full items-center gap-2 rounded-xl border border-emerald-800 px-4 py-2.5 text-sm font-bold text-emerald-900 hover:bg-emerald-50">
                    <Wallet className="h-4 w-4" /> Record Offline Payment
                  </button>
                )}
                {sub?.status === "pending_payment" && (
                  <button onClick={activate} disabled={busy} data-testid="action-activate"
                    className="flex w-full items-center gap-2 rounded-xl bg-emerald-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-50">
                    <BadgeCheck className="h-4 w-4" /> Activate Subscription
                  </button>
                )}
                {sub?.status === "active" && (
                  <button onClick={() => setMode(mode === "change" ? null : "change")} data-testid="action-change-plan"
                    className="flex w-full items-center gap-2 rounded-xl border border-emerald-800 px-4 py-2.5 text-sm font-bold text-emerald-900 hover:bg-emerald-50">
                    <RefreshCcw className="h-4 w-4" /> Change Plan
                  </button>
                )}
                {!canManage && null}
              </div>
            ) : (
              <p className="rounded-xl bg-stone-50 p-3 text-xs text-stone-500" data-testid="billing-readonly-note">
                You have view-only access to subscription and payment information. Plan, payment and activation actions are restricted to Ops Managers and above.
              </p>
            )}

            {canAct && (
              <div className="mt-3 rounded-2xl border border-stone-200 p-4">
                <label className="text-xs font-semibold text-stone-500">Customer status — real customers are closed/archived, never permanently deleted</label>
                <select value={family.status} onChange={(e) => changeStatus(e.target.value)} disabled={busy} data-testid="family-status-select"
                  className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm capitalize">
                  {[...new Set([family.status, "active", "inactive", "closed", "archived"])].filter(Boolean).map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </div>
            )}

            {isSuperAdmin && (
              <div className="mt-3 space-y-2 rounded-2xl border border-red-200 bg-red-50/60 p-4" data-testid="super-admin-zone">
                <p className="text-xs font-bold uppercase tracking-wider text-red-700">Super Admin only</p>
                {(b.parents || []).map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-xl bg-white px-3 py-1.5 text-xs">
                    <span className="font-semibold text-stone-700">{p.name} <span className="text-stone-400">(parent profile)</span></span>
                    <button onClick={() => setConfirmDel(p)} disabled={busy} data-testid={`delete-parent-${p.id}`}
                      className="rounded-full border border-red-300 px-2.5 py-0.5 font-bold text-red-700 hover:bg-red-50">Delete</button>
                  </div>
                ))}
                <button onClick={() => setConfirmDel("family")} disabled={busy} data-testid="action-delete-customer"
                  className="flex w-full items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50">
                  <Trash2 className="h-4 w-4" /> Permanently Delete Customer
                </button>
              </div>
            )}

            {mode === "assign" && (
              <div className="mt-3 space-y-2 rounded-2xl border border-stone-200 p-4" data-testid="assign-plan-form">
                <select value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })} data-testid="assign-plan-select"
                  className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm">
                  <option value="">Select plan…</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {inr(p.price)}/mo</option>)}
                </select>
                <button onClick={assignPlan} disabled={busy || !form.plan_id} data-testid="assign-plan-confirm"
                  className="w-full rounded-full bg-emerald-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Assign plan</button>
              </div>
            )}

            {mode === "payment" && (
              <div className="mt-3 space-y-2 rounded-2xl border border-stone-200 p-4" data-testid="record-payment-form">
                {!sub && (
                  <select value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })} data-testid="payment-plan-select"
                    className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm">
                    <option value="">Select plan…</option>
                    {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {inr(p.price)}/mo</option>)}
                  </select>
                )}
                <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount received (₹, incl. GST)" type="number" data-testid="payment-amount"
                  className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm" />
                <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="payment-method"
                  className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm">
                  {METHODS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                </select>
                <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Transaction / UTR / UPI reference" data-testid="payment-reference"
                  className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm" />
                <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (optional)" data-testid="payment-note"
                  className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm" />
                <button onClick={recordPayment} disabled={busy || (form.amount !== "" && Number(form.amount) <= 0)} data-testid="record-payment-confirm"
                  className="w-full rounded-full bg-emerald-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Record payment & activate</button>
              </div>
            )}

            {mode === "change" && sub?.status === "active" && (
              <div className="mt-3 space-y-2 rounded-2xl border border-stone-200 p-4" data-testid="change-plan-form">
                {plans.filter((p) => p.id !== sub.plan_id).map((p) => (
                  <button key={p.id} onClick={() => requestQuote(p.id)} disabled={busy} data-testid={`change-quote-${p.name.toLowerCase()}`}
                    className="flex w-full items-center justify-between rounded-xl border border-stone-200 px-3 py-2 text-sm hover:border-emerald-700">
                    <span className="font-semibold">{p.name}</span>
                    <span className="text-stone-500">{inr(p.price)}/mo · {p.price > plan.price ? "Upgrade" : "Downgrade"}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {quote && (
          <div className="mt-4 rounded-2xl border border-emerald-300 bg-emerald-50 p-4" data-testid="admin-change-quote">
            <p className="font-heading font-bold text-stone-900">Confirm {quote.type}: {quote.current_plan.name} → {quote.new_plan.name}</p>
            <div className="mt-2 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-stone-500">Payable now (incl. GST)</span><strong>{inr(quote.total_payable_now)}</strong></div>
              <div className="flex justify-between"><span className="text-stone-500">Next renewal (incl. GST)</span><strong>{inr(quote.next_renewal_total)}</strong></div>
              <div className="flex justify-between"><span className="text-stone-500">Effective</span><strong>{quote.type === "upgrade" ? "Immediately" : fmtDay(quote.effective_date)}</strong></div>
            </div>
            {quote.type === "upgrade" && (
              <div className="mt-2 flex gap-2">
                <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="quote-method"
                  className="flex-1 rounded-xl border border-stone-200 px-3 py-2 text-sm">
                  {METHODS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                </select>
                <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Reference" data-testid="quote-reference"
                  className="flex-1 rounded-xl border border-stone-200 px-3 py-2 text-sm" />
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <button onClick={confirmChange} disabled={busy} data-testid="quote-confirm"
                className="rounded-full bg-emerald-900 px-5 py-2 text-sm font-bold text-white disabled:opacity-50">
                {quote.type === "upgrade" ? "Confirm upgrade" : "Schedule downgrade"}
              </button>
              <button onClick={() => setQuote(null)} className="rounded-full border border-stone-200 px-4 py-2 text-sm font-semibold text-stone-500">Cancel</button>
            </div>
          </div>
        )}

        <div className="mt-6">
          <h3 className="mb-2 flex items-center gap-2 font-heading text-sm font-bold text-stone-900"><ReceiptText className="h-4 w-4 text-emerald-800" /> Invoices & receipts</h3>
          {invoices.length === 0 ? <p className="text-sm text-stone-400">No invoices yet.</p> : (
            <div className="space-y-2">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-stone-200 p-3 text-sm" data-testid={`admin-invoice-${inv.id}`}>
                  <span>{inv.number} · {inv.description} <span className="text-xs text-stone-400">· {fmtDay(inv.issued_at)}</span></span>
                  <span className="flex items-center gap-2">
                    <strong>{inr(inv.total)}</strong><StatusPill status={inv.status} />
                    <button onClick={() => downloadReceipt(inv)} aria-label="Download receipt" data-testid={`admin-receipt-${inv.id}`}
                      className="rounded-full border border-stone-200 p-1.5 text-stone-500 hover:text-emerald-900"><Download className="h-3.5 w-3.5" /></button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6">
          <h3 className="mb-2 font-heading text-sm font-bold text-stone-900">Subscription & payment activity</h3>
          {activity.length === 0 ? <p className="text-sm text-stone-400">No activity yet.</p> : (
            <div className="max-h-48 space-y-1.5 overflow-y-auto" data-testid="billing-activity">
              {activity.map((a) => (
                <div key={a.id} className="rounded-xl bg-stone-50 p-2.5 text-xs">
                  <span className="font-bold text-stone-800">{a.action.replace(/_/g, " ")}</span>
                  <span className="text-stone-400"> · {a.actor_email} ({a.actor_role}) · {fmtDay(a.at)}</span>
                  {a.detail && <p className="mt-0.5 text-stone-600">{a.detail}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
        {confirmDel && (
          <ConfirmDelete
            title={confirmDel === "family" ? "Delete this customer account?" : "Delete this parent profile?"}
            body={confirmDel === "family"
              ? `Are you sure you want to permanently delete this account (${family.name})? The family account, all logins, parent profiles, care files, documents, visits, billing and operational records will be removed. This action cannot be undone.`
              : `Are you sure you want to permanently delete this account (${confirmDel.name})? Their care file, visits and linked senior login will be removed. This action cannot be undone.`}
            onConfirm={deleteAccount}
            onCancel={() => setConfirmDel(null)}
            busy={busy}
            testidPrefix="delete-account"
          />
        )}
      </div>
    </div>
  );
}

export default function Customers() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [billingFor, setBillingFor] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const canManage = ["ops_manager", "admin", "super_admin"].includes(user?.role);
  const load = () => api.get("/admin/customers").then((r) => setRows(r.data));
  useEffect(() => { load().catch(() => setRows([])); }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get("id");
    if (id && rows) {
      if (searchParams.get("assign") === "1") openAssign(id);
      else setBillingFor(id);
      setSearchParams({}, { replace: true });
    }
  }, [rows]);
  const openAssign = async (fid) => {
    setAssignFor(fid);
    const r = await api.get(`/admin/assignments/suggest?family_id=${fid}`);
    setSuggestions(r.data);
  };
  const assign = async (cm_id) => {
    try {
      await api.post("/admin/assignments", { family_id: assignFor, cm_id });
      toast.success("Care Manager assigned"); setAssignFor(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  if (!rows) return <Loading />;
  return (
    <div data-testid="admin-customers">
      <PageHeader title="Customers" sub="Families, parents, plans, billing and Care Manager assignments." />
      {rows.length === 0 && <EmptyState title="No customers yet" body="Families appear here after onboarding." />}
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.family.id} className="p1-card flex flex-wrap items-center justify-between gap-4 p-5" data-testid={`customer-row-${r.family.id}`}>
            <div>
              <p className="font-heading font-semibold text-stone-900">{r.family.name}</p>
              <p className="text-sm text-stone-500">{((r.parents && r.parents.length ? r.parents : [r.parent].filter(Boolean))).map((p) => p.name).join(" & ") || "—"} · {r.parent?.zone || r.family.city}</p>
              <p className="text-xs text-stone-400">{r.owner?.name} · {r.owner?.email || r.owner?.phone}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {r.plan && <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-900">{r.plan.name} · {inr(r.plan.price || 0)}/mo</span>}
              {r.subscription && <StatusPill status={r.subscription.status} />}
              <StatusPill status={r.family.status} />
              <span className="text-xs text-stone-500">CM: <strong>{r.care_manager?.name || "Unassigned"}</strong></span>
              <button onClick={() => setBillingFor(r.family.id)} className="rounded-full border border-stone-300 px-4 py-1.5 text-xs font-semibold text-stone-700 hover:border-emerald-700 hover:text-emerald-900" data-testid={`billing-btn-${r.family.id}`}>
                Subscription & Billing
              </button>
              {canManage && (
                <button onClick={() => openAssign(r.family.id)} className="rounded-full border border-emerald-800 px-4 py-1.5 text-xs font-semibold text-emerald-900 hover:bg-emerald-50" data-testid={`assign-btn-${r.family.id}`}>
                  {r.care_manager ? "Reassign CM" : "Assign CM"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {billingFor && <BillingPanel fid={billingFor} canManage={canManage} onClose={() => setBillingFor(null)} onChanged={load} />}

      {assignFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 p-4" data-testid="assign-modal">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <h2 className="font-heading text-xl font-bold text-stone-900">Suggested Care Managers</h2>
            <p className="text-sm text-stone-500">Ranked by area coverage, language, availability, workload and continuity.</p>
            <div className="mt-4 space-y-3">
              {suggestions.map((s) => (
                <div key={s.care_manager.id} className="flex items-center justify-between rounded-2xl border border-stone-200 p-4" data-testid={`suggestion-${s.care_manager.id}`}>
                  <div>
                    <p className="font-heading font-semibold text-stone-900">{s.care_manager.name} <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-800">{s.score}</span></p>
                    <p className="text-xs text-stone-500">{s.reasons.join(" · ")}</p>
                  </div>
                  <button onClick={() => assign(s.care_manager.id)} className="rounded-full bg-emerald-900 px-4 py-2 text-xs font-bold text-white" data-testid={`assign-confirm-${s.care_manager.id}`}>Assign</button>
                </div>
              ))}
            </div>
            <button onClick={() => setAssignFor(null)} className="mt-4 text-sm font-semibold text-stone-500" data-testid="assign-close">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
